import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../hooks/useApi';
import { createInteractiveLesson } from '../../services/api';
import ErrorCard from '../../components/ErrorCard/ErrorCard';
import './InteractiveLessonCreator.css';

const InteractiveLessonCreator = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { handleRequest } = useApi();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    topic: '',
    subjectArea: 'programming',
    learnerProfile: '16–18 year old high school students',
    durationMinutes: 30,
    coursePosition: 'First introduction to the topic',
    lessonLanguage: 'en',
    level: 'basic'
  });

  const subjectAreas = [
    { value: 'programming', label: 'Programmering' },
    { value: 'mathematics', label: 'Matematikk' },
    { value: 'social-science', label: 'Samfunnsfag' },
    { value: 'biology', label: 'Biologi' },
    { value: 'history', label: 'Historie' },
    { value: 'psychology', label: 'Psykologi' },
    { value: 'other', label: 'Annet' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'durationMinutes' 
        ? (value === '' ? '' : parseInt(value) || '') 
        : value
    }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.topic.trim()) {
      setErrorMessage('Vennligst oppgi et emne');
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      const response = await handleRequest(createInteractiveLesson, formData);
      navigate(`/interactive-lessons/${response.data._id || response.data.id}/edit`);
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke opprette interaktiv leksjon');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="interactive-lesson-creator">
      <div className="creator-container">
        <div className="creator-header">
          <button className="back-button" onClick={() => navigate('/dashboard')}>
            ← Tilbake
          </button>
          <h1>Opprett interaktiv leksjon</h1>
        </div>
        <p className="creator-description">
          Generer en interaktiv, skjermbasert leksjon ved hjelp av AI. Leksjonen vil være designet for å gi relevant info og gode eksempler. Elevene kan be om hjelp og få multiple choice spørsmål (og kodeoppgaver) underveis.
        </p>

        {errorMessage && <ErrorCard message={errorMessage} />}

        <form onSubmit={handleSubmit} className="creator-form">
          <div className="form-group">
            <label htmlFor="title">Leksjonstittel (valgfritt)</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="La stå tomt for å bruke AI-generert tittel"
            />
          </div>

          <div className="form-group">
            <label htmlFor="topic">Emne *</label>
            <input
              type="text"
              id="topic"
              name="topic"
              value={formData.topic}
              onChange={handleChange}
              placeholder="f.eks. JavaScript variabler, Fotosyntese, Årsaker til første verdenskrig"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group half-width">
              <label htmlFor="subjectArea">Fagområde *</label>
              <select
                id="subjectArea"
                name="subjectArea"
                value={formData.subjectArea}
                onChange={handleChange}
                required
              >
                {subjectAreas.map(area => (
                  <option key={area.value} value={area.value}>
                    {area.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group half-width">
              <label htmlFor="level">Vanskelighetsgrad *</label>
              <select
                id="level"
                name="level"
                value={formData.level}
                onChange={handleChange}
                required
              >
                <option value="basic">Grunnleggende - For nybegynnere, starter fra grunnleggende konsepter</option>
                <option value="intermediate">Mellomnivå - Noe forkunnskap forventes</option>
                <option value="advanced">Avansert - For erfarne elever</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group half-width">
              <label htmlFor="durationMinutes">Varighet (minutter)</label>
              <input
                type="number"
                id="durationMinutes"
                name="durationMinutes"
                value={formData.durationMinutes}
                onChange={handleChange}
                min="5"
                max="180"
                required
                onBlur={(e) => {
                  // Set to default if empty on blur
                  if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                    setFormData(prev => ({
                      ...prev,
                      durationMinutes: 30
                    }));
                  }
                }}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="learnerProfile">Målgruppe</label>
            <input
              type="text"
              id="learnerProfile"
              name="learnerProfile"
              value={formData.learnerProfile}
              onChange={handleChange}
              placeholder="f.eks. 16–18 år gamle videregående elever med noe kodeerfaring"
            />
          </div>

          <div className="form-group">
            <label htmlFor="coursePosition">Kursplassering</label>
            <input
              type="text"
              id="coursePosition"
              name="coursePosition"
              value={formData.coursePosition}
              onChange={handleChange}
              placeholder="f.eks. Første introduksjon til emnet, Gjennomgang etter tidligere praksis"
            />
          </div>

          <div className="form-group">
            <label htmlFor="lessonLanguage">Språk</label>
            <select
              id="lessonLanguage"
              name="lessonLanguage"
              value={formData.lessonLanguage}
              onChange={handleChange}
            >
              <option value="en">Engelsk</option>
              <option value="no">Norsk</option>
            </select>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={() => navigate('/dashboard')}
            >
              Avbryt
            </button>
            <button
              type="submit"
              className="create-button"
              disabled={loading}
            >
              {loading ? 'Genererer leksjon...' : 'Generer leksjon'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InteractiveLessonCreator;
